import { AppAlertDialog } from '@components/AppAlertDialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog'
import { capitalizeFirstLetter } from '@utils/helper-functions'
import { cn } from '@utils/shad-utils'
import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react'

export interface ConfirmOptions {
  title: string
  description?: ReactNode
  actionLabel?: string
  cancelLabel?: string
}

export interface AlertAction {
  label: string
  onClick: () => void
  variant?: 'primary' | 'secondary' | 'destructive'
}

export interface AlertConfig {
  title: string
  description?: ReactNode
  actions?: AlertAction[]
  onClose?: () => void
}

interface ConfirmState {
  options: ConfirmOptions
  resolve: (value: boolean) => void
}

export interface ModalOptions {
  title?: string
  description?: ReactNode
  dialogContentClassName?: string
  /** Called when the modal is dismissed, whether via closeModal(), the close icon, outside click, or Escape. */
  onClose?: () => void
}

interface ModalState {
  body: ReactNode
  options?: ModalOptions
}

interface DialogContextType {
  openConfirm: (options: ConfirmOptions) => Promise<boolean>
  openAlert: (config: AlertConfig) => void
  closeAlert: () => void
  /** Internal renderer used by useDialog()'s typed openModal — not exported directly. */
  renderModal: (body: ReactNode, options?: ModalOptions) => void
  closeModal: () => void
}

const DialogContext = createContext<DialogContextType | null>(null)

export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null)
  const [alertIsOpen, setAlertIsOpen] = useState(false)
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null)
  const onCloseRef = useRef<(() => void) | undefined>(undefined)
  const [modalState, setModalState] = useState<ModalState | null>(null)
  const modalOnCloseRef = useRef<(() => void) | undefined>(undefined)

  const openConfirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ options, resolve })
    })
  }, [])

  const handleConfirm = () => {
    confirmState?.resolve(true)
    setConfirmState(null)
  }

  const handleCancelConfirm = () => {
    confirmState?.resolve(false)
    setConfirmState(null)
  }

  const openAlert = useCallback((config: AlertConfig) => {
    onCloseRef.current = config.onClose
    setAlertConfig(config)
    setAlertIsOpen(true)
  }, [])

  const closeAlert = useCallback(() => {
    setAlertIsOpen(false)
    const onClose = onCloseRef.current
    onCloseRef.current = undefined
    onClose?.()
    setTimeout(() => {
      setAlertConfig(null)
    }, 200)
  }, [])

  const alertPrimaryAction =
    alertConfig?.actions?.find((a) => a.variant === 'primary') ?? alertConfig?.actions?.[0]
  const alertSecondaryAction = alertConfig?.actions?.find((a) => a.variant === 'secondary')

  const renderModal = useCallback((body: ReactNode, options?: ModalOptions) => {
    modalOnCloseRef.current = options?.onClose
    setModalState({ body, options })
  }, [])

  const closeModal = useCallback(() => {
    setModalState(null)
    const onClose = modalOnCloseRef.current
    modalOnCloseRef.current = undefined
    onClose?.()
  }, [])

  return (
    <DialogContext.Provider value={{ openConfirm, openAlert, closeAlert, renderModal, closeModal }}>
      {children}

      {confirmState && (
        <AppAlertDialog
          open={!!confirmState}
          onOpenChange={(open) => {
            if (!open) handleCancelConfirm()
          }}
          title={confirmState.options.title}
          description={confirmState.options.description ?? ''}
          actionLabel={
            confirmState.options.actionLabel
              ? capitalizeFirstLetter(confirmState.options.actionLabel)
              : undefined
          }
          cancelLabel={
            confirmState.options.cancelLabel
              ? capitalizeFirstLetter(confirmState.options.cancelLabel)
              : undefined
          }
          onActionClick={handleConfirm}
          onCancelClick={handleCancelConfirm}
        />
      )}

      {alertConfig && (
        <AppAlertDialog
          open={alertIsOpen}
          onOpenChange={(open) => {
            if (!open) closeAlert()
          }}
          title={alertConfig.title}
          description={alertConfig.description ?? ''}
          actionLabel={alertPrimaryAction?.label}
          cancelLabel={alertSecondaryAction?.label}
          onActionClick={alertPrimaryAction?.onClick}
          onCancelClick={alertSecondaryAction?.onClick}
        />
      )}

      {modalState && (
        <Dialog
          open={!!modalState}
          onOpenChange={(open) => {
            if (!open) closeModal()
          }}
        >
          <DialogContent
            className={cn('w-full flex flex-col', modalState.options?.dialogContentClassName)}
            {...(!modalState.options?.description && { 'aria-describedby': undefined })}
          >
            {(modalState.options?.title || modalState.options?.description) && (
              <DialogHeader className="gap-1.5">
                {modalState.options?.title && (
                  <DialogTitle className="border-b-0 pb-0 leading-7 font-family-font-sans">
                    {modalState.options.title}
                  </DialogTitle>
                )}
                {modalState.options?.description && (
                  <DialogDescription className="mt-0 leading-normal whitespace-pre-line">
                    {modalState.options.description}
                  </DialogDescription>
                )}
              </DialogHeader>
            )}
            {modalState.body}
          </DialogContent>
        </Dialog>
      )}
    </DialogContext.Provider>
  )
}

export interface UseDialogReturn {
  confirm: (options: ConfirmOptions) => Promise<boolean>
  withConfirm: (
    options: ConfirmOptions,
    onConfirm: () => void,
  ) => (event?: React.BaseSyntheticEvent) => Promise<void>
  alert: (config: AlertConfig) => void
  closeAlert: () => void
  /**
   * Opens an imperative modal for a custom body component, without needing a
   * dedicated Dialog-wrapping component or parent-managed open state.
   *
   * The component is required to declare an `onClose` prop — it is injected
   * automatically (bound to closeModal) so every modal body is guaranteed a
   * way to dismiss itself; you never wire it manually and can't forget it.
   *
   * @example
   * openModal(
   *   ChangeOrganizationNumberModalBody,
   *   { onConfirm: () => void onSubmit() },
   *   { title: t('...title'), description: t('...description') },
   * )
   */
  openModal: <P extends { onClose: () => void }>(
    Component: React.ComponentType<P>,
    props: Omit<P, 'onClose'>,
    options?: ModalOptions,
  ) => void
  closeModal: () => void
}

export const useDialog = (): UseDialogReturn => {
  const context = useContext(DialogContext)

  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider')
  }

  const { openConfirm, openAlert, closeAlert, renderModal, closeModal } = context

  const withConfirm = useCallback(
    (options: ConfirmOptions, onConfirm: () => void) =>
      async (event?: React.BaseSyntheticEvent) => {
        event?.preventDefault()
        event?.stopPropagation()

        const result = await openConfirm(options)

        if (result) {
          onConfirm()
        }
      },
    [openConfirm],
  )

  const openModal = useCallback(
    <P extends { onClose: () => void }>(
      Component: React.ComponentType<P>,
      props: Omit<P, 'onClose'>,
      options?: ModalOptions,
    ) => {
      renderModal(<Component {...(props as P)} onClose={closeModal} />, options)
    },
    [renderModal, closeModal],
  )

  return {
    confirm: openConfirm,
    withConfirm,
    alert: openAlert,
    closeAlert,
    openModal,
    closeModal,
  }
}
